import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Table, TableBody, TableRow, TableCell, TextField, Button, Box, IconButton, Typography, Tooltip } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { Icon } from "@fluentui/react/lib/Icon";
import { propertyConfig } from "../../../constants/ruleProperties";
import { aliasesForDelimiters } from "../../../constants/aliasesForDelimiters";
import PropertyDetailsModal from "./PropertyDetailsModal";
import * as parseRules from "../../../utils/parseRules";
import { emulateTab } from "emulate-tab";
import { RuleRow, escapeValueOperators, shouldTransposeRow, validateRule } from "../../../utils";
import { DeleteRegular, EditRegular } from "@fluentui/react-icons";

export interface ApplicabilityRuleBuilderProps {
  initialName: string;
  initialNameValue?: string;
  initialDelimiter?: string;
  initialValue?: string;
  initialComment?: string;
  isUpdating?: boolean;
  shouldDisplayComment?: boolean;
  shouldDisplayNameValue?: boolean;
  onChange?: (result: { finalValue: string; finalDelimiter: string; comment: string; fullSyntax: string; isValid: boolean }) => void;
  readOnlyViewer?: boolean;
}

export const ApplicabilityRuleBuilder: React.FC<ApplicabilityRuleBuilderProps> = ({
  initialName,
  initialNameValue = "",
  initialDelimiter = "",
  initialValue = "",
  initialComment = "",
  isUpdating = false,
  shouldDisplayComment = true,
  shouldDisplayNameValue = false,
  onChange,
  readOnlyViewer = false,
}) => {
  const initialRules = useMemo(() => {
    if (initialValue && initialValue.trim().length > 0) {
      const finalVal = initialValue || "";
      const textDelimiter = initialDelimiter || "";
      const combined = `${textDelimiter} ${finalVal}`.trim();
      const finalResult = parseRules.getSegmentedRule(combined);

      const rows: RuleRow[] = finalResult.map((seg) => ({
        delimiter: seg.delimiter,
        alias: seg.alias,
        property: seg.property,
        operator: seg.operator,
        value: seg.value,
      }));

      return rows.length > 0
        ? rows
        : [
            {
              delimiter: initialDelimiter,
              alias: initialDelimiter,
              property: "",
              operator: "",
              value: "",
            },
          ];
    }
    return [
      {
        delimiter: initialDelimiter,
        alias: initialDelimiter,
        property: "",
        operator: "",
        value: "",
      },
    ];
  }, [initialValue, initialDelimiter]);

  const [rules, setRules] = useState<RuleRow[]>(initialRules);

  const [comment, setComment] = useState<string>(initialComment);
  const [validationResult, setValidationResult] = useState<{ isValid: boolean; errors: string[] }>({
    isValid: true,
    errors: [],
  });

  const [editingSyntax, setEditingSyntax] = useState("");
  const [editMode, setEditMode] = useState(false);

  const [modalData, setModalData] = useState<{
    open: boolean;
    property: string | null;
  }>({
    open: false,
    property: null,
  });

  const aliasRefs = useRef<(HTMLInputElement | null)[]>([]);
  const valueRefs = useRef<(HTMLInputElement | null)[]>([]);

  const allowedFirstRowAliases = useMemo(() => ["is", "is for any", "="], []);

  const allAliases = useMemo(() => {
    const aliases = Object.keys(aliasesForDelimiters).reduce<string[]>((acc, key) => {
      const arr = aliasesForDelimiters[key].aliases;
      return acc.concat(arr);
    }, []);
    return Array.from(new Set(aliases)).sort((a, b) => a.localeCompare(b));
  }, []);

  const subsequentAliases = useMemo(() => {
    return allAliases.filter((alias) => !allowedFirstRowAliases.includes(alias));
  }, [allAliases, allowedFirstRowAliases]);

  const propertyOptions = useMemo(() => Object.keys(propertyConfig).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())), []);

  useEffect(() => {
    const result = validateRule(rules);
    setValidationResult(result);
  }, [rules]);

  const rowErrors = useMemo(() => {
    return validationResult.errors.reduce((acc, error) => {
      const match = error.match(/Row (\d+):/);
      if (match) {
        const rowIndex = parseInt(match[1], 10) - 1;
        acc[rowIndex] = acc[rowIndex] || [];
        acc[rowIndex].push(error);
      }
      return acc;
    }, [] as string[][]);
  }, [validationResult.errors]);

  const populateGrid = useCallback(
    (customValue: string = "") => {
      const finalVal = customValue || initialValue || "";
      const textDelimiter = customValue ? "" : (initialDelimiter || "").trim();

      const combined = `${textDelimiter} ${finalVal}`.trim();
      const finalResult = parseRules.getSegmentedRule(combined);

      const rows: RuleRow[] = finalResult.map((seg, idx) => ({
        delimiter: seg.delimiter,
        alias: seg.alias,
        property: seg.property,
        operator: seg.operator,
        value: seg.value,
      }));

      if (rows.length > 0) {
        setRules(rows);
      }
    },
    [initialDelimiter, initialValue]
  );

  const adjustAliasRefs = useCallback((newRules: typeof rules) => {
    aliasRefs.current = aliasRefs.current.slice(0, newRules.length);
    valueRefs.current = valueRefs.current.slice(0, newRules.length);
  }, []);

  const handleAddRule = useCallback(() => {
    setRules((prevRules) => {
      const newRule = {
        id: crypto.randomUUID(),   
        delimiter: "",
        alias: "",
        property: "",
        operator: "",
        value: "",
        orGroupId: undefined,      
      };

      const newRules = [...prevRules, newRule];
      adjustAliasRefs(newRules);
      return newRules;
    });

    setTimeout(() => {
      const idx = aliasRefs.current.length - 1;
      aliasRefs.current[idx]?.focus();
    }, 50);
  }, [adjustAliasRefs]);


  const handleDeleteRule = useCallback((index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleInputChange = useCallback((index: number, field: keyof RuleRow, value: string) => {
    setRules((prev) =>
      prev.map((row, i) => {
        if (i === index) {
          if (field === "delimiter") {
            return { ...row, delimiter: value, alias: "" };
          }
          return { ...row, [field]: value };
        }
        return row;
      })
    );
  }, []);

  const handlePropertyClick = useCallback((property: string) => {
    setModalData({ open: true, property });
  }, []);

  const closeModal = useCallback(() => {
    setModalData({ open: false, property: null });
  }, []);

  // What gets written out, not what the grid holds: a value the user typed can
  // contain a word the parser reads as an operator, and the rule would come back
  // split. The text fields keep showing exactly what was typed.
  const safeRules = useMemo(() => escapeValueOperators(rules), [rules]);

  const ruleSyntax = useMemo(() => {
    let syntax = initialName;
    let finalValue = "";
    if (shouldDisplayNameValue) syntax += ` ${initialNameValue}`;
    safeRules.forEach((r, idx) => {
      const transpose = shouldTransposeRow(r);

      syntax += ` ${r.alias}`;
      if (transpose) {
        if (r.value) syntax += ` ${r.value}`;
        if (r.operator) syntax += ` ${r.operator}`;
        if (r.property) syntax += ` ${r.property}`;
      } else {
        if (r.property) syntax += ` ${r.property}`;
        if (r.operator) syntax += ` ${r.operator}`;
        if (r.value) syntax += ` ${r.value}`;
      }

      finalValue += ` ${idx === 0 ? "" : r.alias}`;
      if (transpose) {
        if (r.value) finalValue += ` ${r.value}`;
        if (r.operator) finalValue += ` ${r.operator}`;
        if (r.property) finalValue += ` ${r.property}`;
      } else {
        if (r.property) finalValue += ` ${r.property}`;
        if (r.operator) finalValue += ` ${r.operator}`;
        if (r.value) finalValue += ` ${r.value}`;
      }
    });

    return {
      syntax: syntax.trim(),
      finalValue: finalValue.trim(),
    };
  }, [initialName, initialNameValue, safeRules, shouldDisplayNameValue]);

const getValueText = (r: RuleRow): string => {
  const transpose = shouldTransposeRow(r);
  let text = "";

  if (transpose) {
    if (r.value) text += ` ${r.value}`;
    if (r.operator) text += ` ${r.operator}`;
    if (r.property) text += ` ${r.property}`;
  } else {
    if (r.property) text += ` ${r.property}`;
    if (r.operator) text += ` ${r.operator}`;
    if (r.value) text += ` ${r.value}`;
  }

  return text.trim();
};

  const buildSegmentText = (r: RuleRow): string => {
    const transpose = shouldTransposeRow(r);
    let text = r.alias || "";

    if (transpose) {
      if (r.value) text += ` ${r.value}`;
      if (r.operator) text += ` ${r.operator}`;
      if (r.property) text += ` ${r.property}`;
    } else {
      if (r.property) text += ` ${r.property}`;
      if (r.operator) text += ` ${r.operator}`;
      if (r.value) text += ` ${r.value}`;
    }

    return text.trim();
  };

  const visualSyntaxParts = useMemo(() => {
    const parts: { text: string; isParenthesis: boolean }[] = [];

    // Initial rule name
    let head = initialName;
    if (shouldDisplayNameValue) head += ` ${initialNameValue}`;
    parts.push({ text: head, isParenthesis: false });

    let i = 0;
    while (i < safeRules.length) {
      const current = safeRules[i];
      const alias = current.alias;

      const isOr =
        aliasesForDelimiters["or"].aliases.includes(alias) ||
        aliasesForDelimiters["or not"].aliases.includes(alias);

      // OR group → absorb the PREVIOUS condition
      if (isOr && i > 0) {
        const prev = safeRules[i - 1];

        // render the operator normally (white)
        parts.push({
          text: ` ${prev.alias}`,
          isParenthesis: false,
        });

        // render ONLY the visual "(" in grey
        parts.push({
          text: " (",
          isParenthesis: true,
        });


        // render first value (without operator)
        parts.push({
          text: getValueText(prev),
          isParenthesis: false,
        });

        // render OR chain values
        while (i < safeRules.length) {
          const r = safeRules[i];
          const rAlias = r.alias;

          const stillOr =
            aliasesForDelimiters["or"].aliases.includes(rAlias) ||
            aliasesForDelimiters["or not"].aliases.includes(rAlias);

          if (!stillOr) break;

          parts.push({
            text: ` ${r.alias} ${getValueText(r)}`,
            isParenthesis: false,
          });

          i++;
        }

        parts.push({ text: ")", isParenthesis: true });
        continue;
      }

      // Skip if the NEXT rule is an OR (it will be absorbed)
      const next = safeRules[i + 1];
      const nextIsOr =
        next &&
        (aliasesForDelimiters["or"].aliases.includes(next.alias) ||
          aliasesForDelimiters["or not"].aliases.includes(next.alias));

      if (!nextIsOr) {
        parts.push({
          text: ` ${buildSegmentText(current)}`,
          isParenthesis: false,
        });
      }

      i++;
    }

    return parts;
  }, [initialName, initialNameValue, safeRules, shouldDisplayNameValue]);

    useEffect(() => {
      if (!onChange) return;
      onChange({
        finalValue: ruleSyntax.finalValue,
        finalDelimiter: rules[0]?.alias ?? "",
        comment,
        fullSyntax: ruleSyntax.syntax,
        isValid: validationResult.isValid,
      });
    }, [ruleSyntax, comment, rules, onChange, validationResult.isValid]);

  const handleEditClick = useCallback(() => {
    setEditingSyntax(ruleSyntax.syntax);
    setEditMode(true);
  }, [ruleSyntax.syntax]);

  const handleUpdateRule = useCallback(() => {
    setEditMode(false);

    let custom = editingSyntax;
    if (shouldDisplayNameValue && initialNameValue) {
      const nameValuePattern = new RegExp(`\\s*${initialNameValue}\\s*`);
      custom = custom.replace(nameValuePattern, " ").trim();
    }
    custom = custom.replace(initialName, "").trim();

    populateGrid(custom);
  }, [editingSyntax, initialName, initialNameValue, populateGrid, shouldDisplayNameValue]);

  const handleDiscardEdit = useCallback(() => {
    setEditMode(false);
    setEditingSyntax("");
  }, []);

  const getEnumOptionsFromRegex = (regex?: string): string[] | null => {
    if (!regex) return null;

    const isEnum =
      regex.includes("|") &&
      !regex.includes("[") &&
      !regex.includes("\\") &&
      regex.startsWith("^") &&
      regex.endsWith("$");

    if (!isEnum) return null;

    return regex.replace(/^\^\(\?:?/, "").replace(/\)\$$/, "").split("|");
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === "Enter") {
        handleAddRule();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleAddRule]);

  return (
    <Box mb={10}>
      <Box mb={3}>
        <Typography variant="body1" sx={{ marginBottom: 1, color: "#fff" }}>
          Rule Syntax
          {!editMode && (
            <IconButton onClick={handleEditClick} size="small" color="info" hidden={readOnlyViewer}>
              <EditRegular fontSize={22} />
            </IconButton>
          )}
        </Typography>

        {!editMode && (
          <Box sx={{ backgroundColor: "#333", p: 1, borderRadius: 1, fontFamily: "monospace" }}>
            {visualSyntaxParts.map((part, idx) => (
              <span key={idx} style={{ color: part.isParenthesis ? "#aaa" : "#fff" }}>
                {part.text}
              </span>
            ))}
          </Box>
        )}

        {editMode && (
          <Box mt={1}>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={editingSyntax}
              onChange={(e) => setEditingSyntax(e.target.value)}
              sx={{ backgroundColor: "#333", color: "#fff" }}
            />
            <Box mt={2} display="flex" gap={2}>
              <Button variant="contained" color="primary" onClick={handleUpdateRule}>
                Update Rule
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleDiscardEdit}>
                Discard
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      <Table sx={{ backgroundColor: "#1b1a19", color: "#fff", "& .MuiTableCell-root": { padding: "10px" } }}>
        <TableBody>
          {rules.map((row, idx) => {
            const transpose = shouldTransposeRow(row);
            const hasError = rowErrors[idx] && rowErrors[idx].length > 0;

            return (
              <React.Fragment key={idx}>
                <TableRow
                  sx={{
                    border: hasError ? "2.5px solid #ed7d7d" : "inherit",
                  }}
                >
                  {/* ALIAS */}
                  <TableCell>
                    <Autocomplete
                      freeSolo={false}
                      openOnFocus
                      value={row.alias}
                      disabled={readOnlyViewer}
                      options={idx === 0 ? allowedFirstRowAliases : subsequentAliases}
                      onInputChange={(_, v) => handleInputChange(idx, "alias", v)}
                      onChange={() => setTimeout(() => emulateTab(), 100)}
                      onBlur={(event) => {
                        if (row.alias && !allAliases.includes(row.alias)) {
                          handleInputChange(idx, "alias", "");
                          setTimeout(() => {
                            event.target.focus();
                          }, 0);
                        }
                      }}
                      renderInput={(params) => <TextField {...params} size="small" inputRef={(el) => (aliasRefs.current[idx] = el)} />}
                    />
                  </TableCell>

                  <TableCell>
                    {transpose ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={row.value}
                        onChange={(e) => handleInputChange(idx, "value", e.target.value)}
                        inputRef={(el) => (valueRefs.current[idx] = el)}
                        disabled={row.property.length > 0 && propertyConfig[row.property]?.operators.length === 0}
                      />
                    ) : (
                      <Box display="flex" alignItems="center">
                        <Autocomplete
                          freeSolo={false}
                          openOnFocus
                          disabled={readOnlyViewer}
                          fullWidth
                          value={row.property || null}
                          options={propertyOptions}
                          onInputChange={(_, v) => handleInputChange(idx, "property", v)}
                          onChange={(_, v) => {
                            if (!v || !propertyConfig[v].operators.includes(row.operator)) {
                              const newOperator = v && (v === "installed" || v === "visible installed") ? "is" : "";
                              handleInputChange(idx, "operator", newOperator);
                              if (v && (v === "installed" || v === "visible installed") && newOperator === "is") {
                                setTimeout(() => {
                                  if (valueRefs.current[idx]) {
                                    valueRefs.current[idx]?.focus();
                                  }
                                }, 100);
                              }
                            }
                            if (v && propertyConfig[v].operators.length === 0) {
                              handleInputChange(idx, "value", "");
                            }
                            if (!(v && (v === "installed" || v === "visible installed"))) {
                              setTimeout(() => emulateTab(), 100);
                            }
                          }}
                          onBlur={(event) => {
                            if (row.property && !propertyOptions.includes(row.property)) {
                              handleInputChange(idx, "property", "");
                              setTimeout(() => {
                                event.target.focus();
                              }, 0);
                            }
                          }}
                          renderInput={(params) => <TextField {...params} size="small" />}
                        />
                        {row.property && propertyConfig[row.property] && (
                          <>
                            <Tooltip title={propertyConfig[row.property].description}>
                              <span style={{ display: "inline-flex", marginLeft: 8, cursor: "pointer" }}>
                                <Icon iconName="Info" style={{ color: "#888" }} />
                              </span>
                            </Tooltip>
                            <IconButton sx={{ color: "#888" }} onClick={() => handlePropertyClick(row.property)} tabIndex={-1}>
                              <Icon iconName="View" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    )}
                  </TableCell>

                  {/* OPERATOR */}
                  <TableCell>
                    <Autocomplete
                      freeSolo={false}
                      openOnFocus
                      value={row.operator}
                      options={propertyConfig[row.property]?.operators || []}
                      disabled={!row.property || propertyConfig[row.property]?.operators.length === 0 || readOnlyViewer}
                      onInputChange={(_, v) => handleInputChange(idx, "operator", v)}
                      onChange={() => {
                        setTimeout(() => {
                          if (valueRefs.current[idx]) {
                            valueRefs.current[idx]?.focus();
                          }
                        }, 100);
                      }}
                      onBlur={(event) => {
                        if (
                          propertyConfig[row.property]?.operators.length > 0 &&
                          !propertyConfig[row.property]?.operators.includes(row.operator)
                        ) {
                          handleInputChange(idx, "operator", "");
                          setTimeout(() => {
                            event.target.focus();
                          }, 0);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          disabled={propertyConfig[row.property]?.operators?.length === 0 || !row.property}
                        />
                      )}
                    />
                  </TableCell>

                  <TableCell>
                    {transpose ? (
                      <Box display="flex" alignItems="center">
                        <Autocomplete
                          freeSolo={false}
                          openOnFocus
                          fullWidth
                          value={row.property || null}
                          disabled={readOnlyViewer}
                          options={propertyOptions}
                          onInputChange={(_, v) => handleInputChange(idx, "property", v)}
                          onChange={(_, v) => {
                            if (!v || !propertyConfig[v].operators.includes(row.operator)) {
                              const newOperator = v && (v === "installed" || v === "visible installed") ? "is" : "";
                              handleInputChange(idx, "operator", newOperator);
                              if (v && (v === "installed" || v === "visible installed") && newOperator === "is") {
                                setTimeout(() => {
                                  if (valueRefs.current[idx]) {
                                    valueRefs.current[idx]?.focus();
                                  }
                                }, 100);
                              }
                            }
                            if (v && propertyConfig[v].operators.length === 0) {
                              handleInputChange(idx, "value", "");
                            }
                            if (!(v && (v === "installed" || v === "visible installed"))) {
                              setTimeout(() => emulateTab(), 100);
                            }
                          }}
                          onBlur={(event) => {
                            if (row.property && !propertyOptions.includes(row.property)) {
                              handleInputChange(idx, "property", "");
                              setTimeout(() => {
                                event.target.focus();
                              }, 0);
                            }
                          }}
                          renderInput={(params) => <TextField {...params} size="small" />}
                        />
                        {row.property && propertyConfig[row.property] && (
                          <>
                            <Tooltip title={propertyConfig[row.property].description}>
                              <span style={{ display: "inline-flex", marginLeft: 8, cursor: "pointer" }}>
                                <Icon iconName="Info" style={{ color: "#888" }} />
                              </span>
                            </Tooltip>
                            <IconButton sx={{ color: "#888" }} onClick={() => handlePropertyClick(row.property)} tabIndex={-1}>
                              <Icon iconName="View" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    ) : (
                      (() => {
                        const regex = row.property
                          ? propertyConfig[row.property]?.regex
                          : undefined;

                        const enumOptions = getEnumOptionsFromRegex(regex);

                        const isDisabled =
                          (row.property.length > 0 &&
                            propertyConfig[row.property]?.operators.length === 0) ||
                          readOnlyViewer;

                        if (enumOptions && enumOptions.length > 0) {
                          return (
                            <Autocomplete
                              freeSolo={false}
                              openOnFocus
                              fullWidth
                              value={row.value || null}
                              options={enumOptions}
                              disabled={isDisabled}
                              onChange={(_, v) =>
                                handleInputChange(idx, "value", v || "")
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  inputRef={(el) => (valueRefs.current[idx] = el)}
                                />
                              )}
                            />
                          );
                        }

                        return (
                          <TextField
                            fullWidth
                            size="small"
                            value={row.value}
                            onChange={(e) =>
                              handleInputChange(idx, "value", e.target.value)
                            }
                            disabled={isDisabled}
                            inputRef={(el) => (valueRefs.current[idx] = el)}
                          />
                        );
                      })()
                    )}
                  </TableCell>

                  {/* ACTIONS */}
                  <TableCell>
                    {idx > 0 && (
                      <IconButton tabIndex={-1} onClick={() => handleDeleteRule(idx)} style={{ color: "#d5878b" }} hidden={readOnlyViewer}>
                        <DeleteRegular fontSize={22} style={{ color: "#d5878b" }} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
                {hasError && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ padding: "6px 0px !important", borderBottom: "none" }}>
                      <Box sx={{ color: "#ed7d7d", fontSize: "13px", marginLeft: "10px" }}>
                        {rowErrors[idx].map((error, errorIdx) => (
                          <Typography key={errorIdx} variant="caption" display="block">
                            {error.replace(`Row ${idx + 1}: `, "")}
                          </Typography>
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>

      <Box mt={2} display="flex" justifyContent="flex-start">
        <Button size="small" variant="contained" color="primary" startIcon={<Icon iconName="Add" />} onClick={handleAddRule} hidden={readOnlyViewer}>
          Add Condition
        </Button>
      </Box>

      <Box mt={3} hidden={!shouldDisplayComment}>
        <Typography variant="body1" sx={{ marginBottom: 1, color: "#fff" }}>
          Comment
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Enter comment..."
          value={comment}
          disabled={readOnlyViewer}
          onChange={(e) => setComment(e.target.value)}
          InputProps={{ style: { backgroundColor: "#333", color: "#fff" } }}
        />
      </Box>

      <PropertyDetailsModal
        open={modalData.open}
        onClose={closeModal}
        property={modalData.property}
        description={modalData.property ? propertyConfig[modalData.property]?.description : ""}
        examples={modalData.property ? propertyConfig[modalData.property]?.examples : []}
        related={modalData.property ? propertyConfig[modalData.property]?.related : []}
      />
    </Box>
  );
};
